import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, CalendarDays, ClipboardList, Target, BookMarked,
  PencilRuler, TrendingUp, Crown, User, LogOut, GraduationCap, Zap, Bell, Users,
} from "lucide-react";
import { toast } from "sonner";

function NotificationBell() {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const togglePanel = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try {
        await api.post("/notifications/read-all");
        setNotifs(notifs.map((n) => ({ ...n, read: true })));
      } catch {}
    }
  };

  return (
    <div className="relative">
      <button onClick={togglePanel} data-testid="notifications-bell" className="relative p-2 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors duration-200">
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white rounded-2xl border border-slate-200 shadow-xl z-50 overflow-hidden" data-testid="notifications-panel">
          <p className="px-4 py-3 text-sm font-bold text-slate-900 border-b border-slate-100">Notifications</p>
          <div className="max-h-80 overflow-y-auto">
            {notifs.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-400 text-center">Aucune notification pour l'instant.</p>
            ) : (
              notifs.map((n) => (
                <div key={n.id} className={`px-4 py-3 border-b border-slate-50 ${n.read ? "" : "bg-indigo-50/50"}`} data-testid={`notification-${n.id}`}>
                  <p className="text-sm font-semibold text-slate-800">{n.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord", testid: "nav-dashboard" },
  { to: "/timetable", icon: CalendarDays, label: "Emploi du temps", testid: "nav-timetable" },
  { to: "/homework", icon: ClipboardList, label: "Devoirs", testid: "nav-homework" },
  { to: "/goals", icon: Target, label: "Objectifs", testid: "nav-goals" },
  { to: "/revisions", icon: BookMarked, label: "Révisions", testid: "nav-revisions" },
  { to: "/flashcards", icon: Zap, label: "Flashcards", testid: "nav-flashcards" },
  { to: "/exercises", icon: PencilRuler, label: "Exercices", testid: "nav-exercises" },
  { to: "/progress", icon: TrendingUp, label: "Progression", testid: "nav-progress" },
  { to: "/pricing", icon: Crown, label: "Tarifs", testid: "nav-pricing" },
  { to: "/profile", icon: User, label: "Profil", testid: "nav-profile" },
];

const PLAN_BADGE = {
  gratuit: "bg-slate-100 text-slate-700",
  basique: "bg-indigo-100 text-indigo-700",
  premium: "bg-gradient-to-r from-indigo-600 to-violet-600 text-white",
};

const PARENT_NAV = [
  { to: "/parent", icon: Users, label: "Mes enfants", testid: "nav-parent" },
  { to: "/profile", icon: User, label: "Profil", testid: "nav-profile" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    toast.success("À bientôt !");
    navigate("/");
  };

  const plan = user?.plan || "gratuit";
  const isParent = user?.role === "parent";
  const navItems = isParent ? PARENT_NAV : NAV;

  if (isParent && !["/parent", "/profile", "/pricing"].includes(location.pathname)) {
    return <Navigate to="/parent" replace />;
  }
  if (!isParent && location.pathname === "/parent") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFC]" data-testid="app-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white border-r border-slate-200/80 z-40" data-testid="sidebar">
        <div className="flex items-center justify-between px-6 h-16 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-500 flex items-center justify-center">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight text-slate-900">ProEdu</span>
          </div>
          <NotificationBell />
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navItems.map(({ to, icon: Icon, label, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-sm">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${PLAN_BADGE[plan]}`} data-testid="sidebar-plan-badge">
                {plan}
              </span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-sm font-medium text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors duration-200"
          >
            <LogOut className="h-4 w-4" />
            Se déconnecter
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200/60" data-testid="mobile-header">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-500 flex items-center justify-center">
              <GraduationCap className="h-4 w-4 text-white" />
            </div>
            <span className="font-display font-bold tracking-tight text-slate-900">ProEdu</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${PLAN_BADGE[plan]}`} data-testid="mobile-plan-badge">
              {plan}
            </span>
            <button onClick={handleLogout} data-testid="mobile-logout-button" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="lg:pl-64 pb-24 lg:pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/70" data-testid="mobile-bottom-nav">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${Math.min(navItems.length, 5)}, 1fr)` }}>
          {navItems.slice(0, 5).map(({ to, icon: Icon, label, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`mobile-${testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition-colors duration-200 ${
                  isActive ? "text-indigo-600" : "text-slate-500"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              {label.split(" ")[0]}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
